import type {
  DataTemplateType,
  IBounds,
  IDataObjectType,
  IMaterialRun,
  IMeasurementRun,
} from "../data-object";
import type { GenerationAlgorithm } from "../gem-table-config";
import type { GEMDSnapshotQuery } from "../gemd-snapshot";
import type { MaterialRunClassification } from "../table-config";

export const OBJECT_TYPES: (DataTemplateType | IDataObjectType)[] = [
  "condition_template",
  "ingredient_run",
  "ingredient_spec",
  "material_run",
  "material_spec",
  "material_template",
  "measurement_run",
  "measurement_spec",
  "measurement_template",
  "parameter_template",
  "process_run",
  "process_spec",
  "process_template",
  "property_template",
] as const;
export type ObjectType = (typeof OBJECT_TYPES)[number];

export interface TerminalMaterialAttribute<Bounds extends IBounds = IBounds> {
  attribute_template_name: string;
  attribute_template_id: string;
  dataset_id: string;
  bounds: Bounds;
  count: number;
  created_at?: number;
  created_by?: string;
  updated_at?: number;
  updated_by?: string;
}

export interface GemdDataQuery extends GEMDSnapshotQuery {
  name: string;
}

export interface GenerateTableConfigRequest {
  query: GemdDataQuery;
  algorithm?: GenerationAlgorithm;
}

export interface SampleCountResponse {
  terminal_material_count: number;
}

interface BasePageParams {
  forward: boolean;
  ascending: boolean;
}

export interface PageParams extends BasePageParams {
  cursor: string;
  per_page: number;
}

export interface JsonApiPagination {
  number: number;
  size: number;
  total_count: number;
  total_pages: number;
}

export interface JsonApiLinks {
  self: string;
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface JsonApiResource<T> {
  id: string;
  type: string;
  attributes: T;
}

export interface JsonApiResponse<T> {
  data: JsonApiResource<T>[];
  meta: { page: JsonApiPagination };
  links: JsonApiLinks;
}

export interface MaterialsAndMeasurementsMetadata {
  page: JsonApiPagination;
}

export type SortOrder = "asc" | "desc";

export interface SortEntry {
  field: string;
  order: SortOrder;
}

export interface MaterialsQueryParams {
  pageNumber: number;
  pageSize: number;
  sort?: SortEntry[];
}

export const EXTENDED_MATERIAL_CLASSIFICATIONS = {
  raw: "raw_material",
  intermediate: "intermediate_material",
  isolated: "isolated_material", // This is untested ingredient
  terminal: "terminal_material",
} as const;

export type ExtendedMaterialClassification =
  (typeof EXTENDED_MATERIAL_CLASSIFICATIONS)[keyof typeof EXTENDED_MATERIAL_CLASSIFICATIONS];

export interface MaterialAndMeasurements {
  material_run: IMaterialRun;
  measurement_runs: IMeasurementRun[];
  classification: MaterialRunClassification;
  extended_material_classification: ExtendedMaterialClassification;
}

export interface MaterialAndMeasurementsPageResult {
  materials: MaterialAndMeasurements[];
  meta: MaterialsAndMeasurementsMetadata;
}

export interface MaterialTag {
  classifications: ExtendedMaterialClassification[];
  tag: string;
}

export const MATERIAL_ATTRIBUTE_QUERY = {
  isolated: { is_material_consumed: false, is_material_produced: false }, // Untested ingredients
  raw_and_isolated: { is_material_produced: false },
  terminal: { is_material_consumed: false, is_material_produced: true },
} as const;
